import React, { useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { useT } from "@/lib/LanguageContext";

/**
 * The reply box. Owns the POST, because "sending" is the only state the textarea has that
 * the thread above does not: the thread is told about a message once it EXISTS
 * (`onSent`), and never about a draft.
 *
 * Mirrors `modules/messaging/schemas.MessageCreate`: at most 5 000 characters, not blank.
 * Both are enforced here as well as there — not to be clever, but because a 422 on a long
 * reply loses the reply, and the counter has to be counting against the real ceiling for
 * the number on screen to mean anything.
 */
export const BODY_MAX = 5000;

export default function Composer({ conversationId, onSent }) {
  const t = useT("admin");
  const tc = useT("common");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const tooLong = body.length > BODY_MAX;
  const canSend = body.trim().length > 0 && !tooLong && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/conversations/${conversationId}/messages`, { body });
      // Cleared only on a 201: a failed send must leave the text where the practitioner
      // can retry it, not swallow what they wrote.
      setBody("");
      onSent(data);
    } catch (e) {
      toast.error(extractApiError(e, tc("errors.saveFailed"), tc));
    } finally {
      setSending(false);
    }
  };

  // ⌘/Ctrl+Enter sends; a bare Enter is a newline, because a reply to a client is prose
  // and not a chat line.
  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border-t border-eh-border-soft pt-4 mt-4" data-testid="admin-message-composer">
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">
        {t("msg.reply")}
      </label>
      <textarea
        data-testid="admin-message-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={sending}
        rows={4}
        placeholder={t("msg.replyPlaceholder")}
        className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2 text-sm text-eh-ink outline-none focus:border-eh-sage-deep disabled:opacity-60"
      />
      <div className="flex items-center justify-between gap-3 mt-2">
        <span
          data-testid="admin-message-count"
          className={`text-xs ${tooLong ? "text-red-700" : "text-eh-muted"}`}
        >
          {t("msg.charCount", { n: body.length, max: BODY_MAX })}
        </span>
        <button
          type="button"
          data-testid="admin-message-send"
          onClick={send}
          disabled={!canSend}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}{" "}
          {sending ? t("msg.sending") : t("msg.send")}
        </button>
      </div>
    </div>
  );
}
